import { useCallback } from 'react'
import Link from 'next/link'
import BidLocationIcon from '../BidLocationIcon/BidLocationIcon'

const GREEN_CONDITIONS = ['Brand New', 'Appears New', 'New/Like New']

type ThemeClassNames = {
  hollow: string
  solid: string
  location: string
}

const EMPTY_THEME_CLASSES: ThemeClassNames = {
  hollow: '',
  solid: '',
  location: '',
}

const GREEN_THEME_CLASSES: ThemeClassNames = {
  hollow: 'border border-darkgreen text-darkgreen bg-white',
  solid: 'bg-darkgreen text-white',
  location: 'text-white',
}

const THEME_CLASS_MAP: Record<string, ThemeClassNames> = {
  blue: {
    hollow: 'border border-bidfta-blue text-bidfta-blue bg-white',
    solid: 'bg-bidfta-blue text-white',
    location: 'text-white',
  },
  'blue-light': {
    hollow: 'border border-bidfta-blue-light text-bidfta-blue-light bg-white',
    solid: 'bg-bidfta-blue-light text-white',
    location: 'text-bidfta-blue-light',
  },
  'blue-dark': {
    hollow: 'border border-bidfta-blue-dark text-bidfta-blue-dark bg-white',
    solid: 'bg-bidfta-blue-dark text-white',
    location: 'text-bidfta-blue-dark',
  },
  red: {
    hollow: 'border border-bidfta-red text-bidfta-red bg-white',
    solid: 'bg-bidfta-red text-white',
    location: 'text-red',
  },
}

const buildThemeClasses = (
  theme: string,
  isGreen: boolean
): ThemeClassNames => {
  if (isGreen) {
    return GREEN_THEME_CLASSES
  }

  return THEME_CLASS_MAP[theme] ?? EMPTY_THEME_CLASSES
}

export default function BidLabel({
  type,
  value,
  labelStyle,
  theme,
  children,
  twcss,
  fontStyles,
  link,
  fullWidth,
}: any) {
  const applyTheme = useCallback(
    (isHollow: boolean, isLocation?: boolean) => {
      const isGreen = GREEN_CONDITIONS.includes(value)
      const themeClasses = buildThemeClasses(theme, isGreen)

      if (isLocation) {
        return themeClasses.location
      }

      return isHollow ? themeClasses.hollow : themeClasses.solid
    },
    [theme, value]
  )

  return (
    <>
      {link ? (
        <Link href={link}>
          <div
            className={`px-3 py-2 flex items-center ${
              labelStyle === 'hollow' ? applyTheme(true) : applyTheme(false)
            } ${twcss || ''} ${fullWidth ? 'w-full h-auto' : 'w-max h-8'}`}
          >
            {type === 'location' && (
              <div className="mr-3 inline-block">
                <BidLocationIcon
                  fill={
                    labelStyle === 'hollow' ? applyTheme(true, true) : '#FFF'
                  }
                />
              </div>
            )}
            <span className="text-xs">{value || children}</span>
          </div>
        </Link>
      ) : (
        <div
          className={`px-3 py-1.5 flex items-center ${
            labelStyle === 'hollow' ? applyTheme(true) : applyTheme(false)
          } ${twcss || ''} ${fullWidth ? 'w-full h-auto' : 'w-max h-8'}`}
        >
          {type === 'location' && (
            <div className="mr-3 inline-block">
              <BidLocationIcon
                fill={labelStyle === 'hollow' ? applyTheme(true, true) : '#FFF'}
              />
            </div>
          )}
          <span className={`font-bold ${fontStyles || 'text-xs'}`}>
            {value || children}
          </span>
        </div>
      )}
    </>
  )
}

/*
(required) value = String to be shown in label, children below can be used instead of value
(required) theme = "blue", "blue-dark", "blue-light", "gray" etc... NOTE: Need to extend the applyTheme() function
if trying to render a color that isn't in it
(optional) children = children can be used used instead of a value above
(optional) type = "location" - a location mark will be shown in the label
(optional) labelStyle = default "solid", "hollow"
(optional) twcss = "md:hidden uppercase"
(optional) link = "/"
(optional) fontStyles = "text-10" - styles applied directly to text

<BidLabel
    value="Seattle, WA"
    link="/"
    theme="blue-dark"
    type="location"
    labelStyle="hollow"
/>
*/
